import { ensureSingleScript } from '@snapchat/mw-common/client';
import type { CookieAcceptance } from '@snapchat/mw-cookie-components';

import type { ShopifyAccess } from '../../configTypes';

export const shopifyConsentTimeoutMs = 5_000;

type ShopifyFeatureError = Error | string | null | undefined;

interface ShopifyTrackingConsentPayload {
  marketing: boolean;
  analytics: boolean;
  preferences: boolean;
  /**
   * Shopify's CCPA field: `true` = user allows data sale, `false` = user opts out.
   * Derived from the Global Privacy Control (GPC) browser signal
   * (`Sec-GPC` header / `navigator.globalPrivacyControl`). When GPC is active the
   * user is opting out of data sale per CCPA. This mirrors how our GA4 logging
   * handles GPC on both MWP (`GoogleEventListener`) and the Shopify pixel
   * (`shopify-logging` `logger.ts`).
   */
  sale_of_data: boolean;
  headlessStorefront: true;
  checkoutRootDomain: string;
  storefrontRootDomain: string;
  storefrontAccessToken: string;
}

interface ShopifyCustomerPrivacyApi {
  setTrackingConsent: (payload: ShopifyTrackingConsentPayload, callback?: () => void) => void;
}

interface ShopifyWindowApi {
  loadFeatures?: (
    features: Array<{ name: string; version: string }>,
    callback: (error: ShopifyFeatureError) => void
  ) => void;
  customerPrivacy?: ShopifyCustomerPrivacyApi;
}

declare global {
  interface Window {
    Shopify?: ShopifyWindowApi;
  }
}

let consentApiPromise: Promise<ShopifyCustomerPrivacyApi> | undefined;

/**
 * Loads and caches the Shopify customerPrivacy API. On the first call, this loads the consent
 * tracking script, initializes the `consent-tracking-api` feature, and returns the resulting API.
 * Subsequent calls return the cached promise. The cache is cleared on failure so the next call
 * retries from scratch.
 */
const ensureConsentApi = (): Promise<ShopifyCustomerPrivacyApi> => {
  if (consentApiPromise) return consentApiPromise;

  consentApiPromise = (async () => {
    if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
      return window.Shopify.customerPrivacy;
    }

    await new Promise<void>(resolve => {
      ensureSingleScript(
        'shopify-consent-tracking-api',
        'https://cdn.shopify.com/shopifycloud/consent-tracking-api/v0.1/consent-tracking-api.js',
        resolve
      );
    });

    if (window.Shopify?.customerPrivacy?.setTrackingConsent) {
      return window.Shopify.customerPrivacy;
    }

    const shopify = window.Shopify;

    if (!shopify?.loadFeatures) {
      throw new Error('Shopify consent tracking API failed to initialize');
    }

    await new Promise<void>((resolve, reject) => {
      shopify.loadFeatures!(
        [{ name: 'consent-tracking-api', version: '0.1' }],
        (error: ShopifyFeatureError) => {
          if (error) {
            reject(error instanceof Error ? error : new Error(String(error)));
            return;
          }

          resolve();
        }
      );
    });

    if (!window.Shopify?.customerPrivacy?.setTrackingConsent) {
      throw new Error('Shopify customerPrivacy API unavailable after loading consent-tracking-api');
    }

    return window.Shopify.customerPrivacy;
  })().catch(error => {
    consentApiPromise = undefined;
    throw error;
  });

  return consentApiPromise;
};

/** Maps cookie acceptance categories to the payload shape Shopify's consent API expects. */
const getShopifyTrackingConsentPayload = (
  cookieAcceptance: CookieAcceptance,
  store: ShopifyAccess,
  saleOfData: boolean
): ShopifyTrackingConsentPayload => {
  // Shopify expects both domain fields even though Stinson uses the same
  // domain for the storefront and checkout today.
  const rootDomain = store.domain;

  return {
    marketing: cookieAcceptance.Marketing === true,
    analytics: cookieAcceptance.Performance === true,
    preferences: cookieAcceptance.Preferences === true,
    sale_of_data: saleOfData,
    headlessStorefront: true,
    checkoutRootDomain: rootDomain,
    storefrontRootDomain: rootDomain,
    storefrontAccessToken: store.storefrontAccessToken,
  };
};

/**
 * Submits tracking consent preferences to Shopify for the given store. Loads the Shopify consent
 * API if it hasn't been loaded yet. The entire operation (loading + submission) is bounded by
 * {@link shopifyConsentTimeoutMs} by default.
 *
 * @param cookieAcceptance - The accepted cookie categories from the MWP cookie modal event.
 * @param store - The Shopify store config (domain + storefront access token).
 * @param saleOfData - Whether the user allows sale of their data. Defaults to `true`.
 *   Pass `false` when the GPC signal is active to opt the user out per CCPA.
 * @param timeoutMs - Total timeout budget for loading the API and submitting consent.
 */
export const submitShopifyTrackingConsent = async ({
  cookieAcceptance,
  store,
  saleOfData = true,
  timeoutMs = shopifyConsentTimeoutMs,
}: {
  cookieAcceptance: CookieAcceptance;
  store: ShopifyAccess;
  saleOfData?: boolean;
  timeoutMs?: number;
}): Promise<void> => {
  await Promise.race([
    (async () => {
      const api = await ensureConsentApi();
      const payload = getShopifyTrackingConsentPayload(cookieAcceptance, store, saleOfData);
      await new Promise<void>(resolve => api.setTrackingConsent(payload, resolve));
    })(),
    new Promise<never>((_, reject) => {
      window.setTimeout(
        () => reject(new Error(`Shopify tracking consent timed out after ${timeoutMs}ms`)),
        timeoutMs
      );
    }),
  ]);
};
